import { Building2 } from 'lucide-react';
import type { ZefixCompanyPrefill } from '@bill-alps/backend/features/zefix/models';
import { cn } from '@bill-alps/ui';

import { useTranslation } from '../i18n/useTranslation';

/**
 * Read-only recap of what a Zefix pick filled in, shown under the search
 * field so the user confirms the register data before moving on. The "enter
 * manually" link drops it and leaves the typed name alone.
 */
export function ZefixPrefillSummary({
  prefill,
  onClear,
  className,
}: {
  prefill: ZefixCompanyPrefill;
  onClear: () => void;
  className?: string;
}) {
  const { t } = useTranslation('zefix');

  const streetLine = [prefill.address?.street, prefill.address?.streetNumber]
    .filter(Boolean)
    .join(' ');
  const cityLine = [prefill.address?.postalCode, prefill.address?.city]
    .filter(Boolean)
    .join(' ');

  const rows: Array<{ label: string; value: string }> = [
    ...(prefill.ide
      ? [{ label: t('zefix:prefill.ide'), value: prefill.ide }]
      : []),
    ...(streetLine || cityLine
      ? [
          {
            label: t('zefix:prefill.address'),
            value: [streetLine, cityLine].filter(Boolean).join(', '),
          },
        ]
      : []),
    ...(prefill.canton
      ? [{ label: t('zefix:prefill.canton'), value: prefill.canton }]
      : []),
  ];

  if (rows.length === 0) return null;

  return (
    <div
      className={cn(
        'rounded-xl border-2 border-border bg-muted/40 p-3 text-sm',
        className,
      )}
    >
      <div className="flex items-center gap-2 font-medium">
        <Building2 className="size-4 shrink-0 text-muted-foreground" />
        {t('zefix:prefill.title')}
      </div>
      <dl className="mt-2 space-y-1">
        {rows.map((row) => (
          <div key={row.label} className="flex gap-2">
            <dt className="shrink-0 text-muted-foreground">{row.label}</dt>
            <dd className="min-w-0 break-words">{row.value}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[11px] text-muted-foreground">
          {t('zefix:prefill.source')}
        </span>
        <button
          type="button"
          onClick={onClear}
          className="text-xs underline underline-offset-2 hover:no-underline"
        >
          {t('zefix:prefill.clear')}
        </button>
      </div>
    </div>
  );
}
